import type { JwtToken } from '../types/jwt.type';

/**
 * Crea un objeto JwtToken a partir de un string, validando su formato y asegurando que no esté vacío.
 * @param value El string del token JWT
 * @returns Un objeto JwtToken con el valor proporcionado
 * @throws Error si el token es vacío o no tiene un formato válido
 */
export function createJwtToken(value: string): JwtToken {
  if (!value || value.trim().length === 0) {
    throw new Error('JWT token cannot be empty');
  }

  if (!isValidJwtFormat(value)) {
    throw new Error('Invalid JWT token format');
  }

  return { value };
}

/**
 * Valida que un string tenga el formato de un JWT (tres partes separadas por puntos, con caracteres válidos)
 * @param token El string del token JWT a validar
 * @returns true si el formato es válido, false en caso contrario
 * @throws Error si el token no es una cadena de texto
 */
function isValidJwtFormat(token: string): boolean {
  const jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/;
  return jwtRegex.test(token);
}

/**
 * Verifica si un token JWT ha expirado comparando su tiempo de expiración con el tiempo actual.
 * @param token El objeto JwtToken a verificar
 * @returns true si el token ha expirado, false si aún es válido o si no se pudo verificar su formato/expiración
 */
export function isTokenExpired(token: JwtToken): boolean {
  try {
    const payload = getTokenPayload(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp < currentTime;

    console.log('🔍 [JWT] Token expiration check', {
      exp: payload.exp,
      currentTime,
      expiresIn: payload.exp - currentTime,
      isExpired,
      expirationDate: new Date(payload.exp * 1000).toISOString(),
      currentDate: new Date(currentTime * 1000).toISOString(),
    });

    return isExpired;
  } catch (error) {
    console.error('❌ [JWT] Error checking token expiration:', error);
    return true;
  }
}

/**
 * Extrae y decodifica el payload de un token JWT, retornándolo como un objeto JavaScript.
 * @param token El objeto JwtToken del cual extraer el payload
 * @returns El payload decodificado como un objeto
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getTokenPayload(token: JwtToken): any {
  const parts = token.value.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }

  const payload = parts[1];
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const decodedPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(decodedPayload);
}

/**
 * Extrae el userId (o sub) del payload de un token JWT, que representa la identidad del usuario.
 * @param token El objeto JwtToken del cual extraer el userId
 * @returns El userId extraído del token, o null si no se encuentra en el payload
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
export function getTokenUserId(token: JwtToken): string {
  const payload = getTokenPayload(token);
  return payload.sub || payload.userId;
}

/**
 * Extrae la lista de permisos del payload de un token JWT, que representa las autorizaciones del usuario.
 * @param token El objeto JwtToken del cual extraer los permisos
 * @returns Un array de strings con los permisos extraídos del token, o un array vacío si no se encuentran en el payload
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
export function getTokenPermissions(token: JwtToken): string[] {
  const payload = getTokenPayload(token);
  return payload.permissions || [];
}

/**
 * Extrae el rol del usuario del payload de un token JWT, que puede estar representado como un string (formato antiguo) o como un objeto con roleId (formato nuevo).
 * @param token El objeto JwtToken del cual extraer el rol
 * @returns El rol extraído del token, o null si no se encuentra en el payload
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
export function getTokenRole(token: JwtToken): string | null {
  const payload = getTokenPayload(token);
  // Compatibilidad: si role es string (formato anterior), retornarlo
  if (typeof payload.role === 'string') {
    return payload.role;
  }
  // Si role es objeto (formato nuevo), retornar el roleId
  return payload.role?.roleId || null;
}

/**
 * Extrae el roleId del rol del usuario del payload de un token JWT, que puede estar representado como un string (formato antiguo) o como un objeto con roleId (formato nuevo).
 * @param token El objeto JwtToken del cual extraer el roleId
 * @returns El roleId extraído del token, o null si no se encuentra en el payload
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
export function getTokenRoleId(token: JwtToken): string | null {
  const payload = getTokenPayload(token);
  // Si role es objeto (formato nuevo)
  if (typeof payload.role === 'object' && payload.role?.roleId) {
    return payload.role.roleId;
  }
  // Si role es string (formato anterior)
  if (typeof payload.role === 'string') {
    return payload.role;
  }
  return null;
}

/**
 * Extrae el nombre del rol del usuario del payload de un token JWT, que solo estará presente en el formato nuevo donde role es un objeto con name.
 * @param token El objeto JwtToken del cual extraer el nombre del rol
 * @returns El nombre del rol extraído del token, o null si no se encuentra en el payload o si el formato es antiguo
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
export function getTokenRoleName(token: JwtToken): string | null {
  const payload = getTokenPayload(token);
  // Si role es objeto (formato nuevo)
  if (typeof payload.role === 'object' && payload.role?.name) {
    return payload.role.name;
  }
  return null;
}

/**
 * Calcula el tiempo restante en milisegundos hasta la expiración de un token JWT, comparando su tiempo de expiración (exp) con el tiempo actual.
 * @param token El objeto JwtToken del cual calcular el tiempo hasta la expiración
 * @returns El tiempo restante en milisegundos hasta la expiración del token, o un número negativo si ya ha expirado o si no se pudo verificar su formato/expiración
 * @throws Error si el token no tiene un formato válido o si el payload no se puede decodificar
 */
export function getTokenTimeUntilExpiration(token: JwtToken): number {
  const payload = getTokenPayload(token);
  const currentTime = Date.now();
  return payload.exp * 1000 - currentTime;
}
